// The assistant's session surface — ONE owner per name, in shared.
//
// ONE SHELL ASSISTANT (Nika, 2026-10-03): the shell has one assistant and one
// chat list on every page; preview standalone keeps its own. Shared's Chat /
// SessionMenu / AiContextBar / VoiceOverlay call these names on every host
// (`el.call('sendPrompt', …)`), and two hosts implement them: the shell and
// preview. They share one bag — the shell spreads preview's functions into
// its own context and folds preview's full context on /preview, and the
// workspace-ui catalog merges both libraries — so a host registering the bare
// name meant the merge order picked the assistant.
//
// So shared registers each name here, and each host registers its
// implementation under its own prefix (`shellSendPrompt`, `previewSendPrompt`,
// …). The dispatcher answers with the SHELL's implementation whenever it is in
// the calling element's bag, else preview's: that is exactly the product rule
// (inside the shell the shell's assistant owns every page; preview standalone
// has no shell). Call sites never change.
//
// Contract: `this` stays the calling element, every argument passes, the
// implementation's return value (a promise for the async ones) returns
// unchanged. A host that misses a name is a hard error, never a silent no-op:
// console.error, and a rejected promise for the async names. The one
// synchronous name, buildAiAwareness (read during render), returns undefined
// after the console.error — the same value an unregistered el.call returns —
// because a promise in its place throws inside every render that reads it.
//
// A page with NO host at all is not a wiring bug. A site can link the shared
// library for its components and have no assistant session: the docs site
// links system/shared, mounts its own chat with its own sendPrompt, and has
// no shell or preview implementation of any name. Its chat calls
// hydrateServerSessions on mount, and the hard error was an uncaught page
// error on every docs page (2026-10-28). There is nothing to load or act on
// there, so every name answers quietly: the async names resolve undefined,
// buildAiAwareness returns undefined, nothing reaches the console.

// Kept self-contained (no module-scope reads) so frank serializes it whole.
const _aiSessionDispatch = (el, name, args, sync) => {
  const fns = (el && el.context && el.context.functions) || {}
  const hosts = ['shell', 'preview']
  const suffix = name.charAt(0).toUpperCase() + name.slice(1)
  for (const host of hosts) {
    const impl = fns[host + suffix]
    if (typeof impl === 'function') return impl.apply(el, args)
  }
  // A host is in the bag when it registered any of the seven names below
  // (the names this module exports, as their prefixed suffix).
  const suffixes = [
    'SendPrompt',
    'NewSession',
    'SwitchSession',
    'DeleteSession',
    'HydrateServerSessions',
    'RestoreActiveThread',
    'BuildAiAwareness'
  ]
  const hosted = hosts.some((host) => suffixes.some((s) => typeof fns[host + s] === 'function'))
  if (!hosted) return sync ? undefined : Promise.resolve()
  const error = new Error(
    `[aiSession] no host implements ${name} (looked for shell${suffix}, preview${suffix})`
  )
  console.error(error.message)
  return sync ? undefined : Promise.reject(error)
}

export const sendPrompt = function (...args) {
  return _aiSessionDispatch(this, 'sendPrompt', args)
}
export const newSession = function (...args) {
  return _aiSessionDispatch(this, 'newSession', args)
}
export const switchSession = function (...args) {
  return _aiSessionDispatch(this, 'switchSession', args)
}
export const deleteSession = function (...args) {
  return _aiSessionDispatch(this, 'deleteSession', args)
}
export const hydrateServerSessions = function (...args) {
  return _aiSessionDispatch(this, 'hydrateServerSessions', args)
}
export const restoreActiveThread = function (...args) {
  return _aiSessionDispatch(this, 'restoreActiveThread', args)
}
export const buildAiAwareness = function (...args) {
  return _aiSessionDispatch(this, 'buildAiAwareness', args, true)
}
